import { Link } from 'react-router-dom';
import { ChevronRight, Pencil } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth-context';
import type { ChecklistTemplate } from '@/lib/types';

/**
 * Per-domain gallery of checklist templates. Pure presentation —
 * the parent owns the data fetch and the domain-id-to-name lookup.
 *
 * Editable templates (caller is owner/admin AND template belongs to
 * the active account) get a pencil affordance in the top-right.
 * Platform templates (account_id NULL) are read-only for everyone
 * and never show the pencil.
 */
export default function TemplateGallery({
  templates,
  domainName,
}: {
  templates: ChecklistTemplate[];
  domainName: (id: string) => string;
}) {
  const { account, role } = useAuth();
  const canEdit = role === 'owner' || role === 'admin';

  const grouped = new Map<string, ChecklistTemplate[]>();
  for (const t of templates) {
    const list = grouped.get(t.domainId) ?? [];
    list.push(t);
    grouped.set(t.domainId, list);
  }

  return (
    <div className="space-y-6">
      {[...grouped.entries()].map(([domainId, list]) => (
        <div key={domainId} className="space-y-2">
          <h3 className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {domainName(domainId)}
          </h3>
          <ul className="grid gap-3 md:grid-cols-2">
            {list.map((t) => {
              const ownedByMe = canEdit && t.accountId !== null && t.accountId === account?.id;
              return (
                <li key={t.id} className="relative">
                  <Link
                    to={`/checklists/templates/${t.slug}`}
                    className="group block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid={`template-${t.slug}`}
                  >
                    <Card className="h-full border-rule transition-all group-hover:-translate-y-0.5 group-hover:border-primary/30 group-hover:shadow-md">
                      <CardHeader className="space-y-1.5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {t.slug}
                        </p>
                        <div className="flex items-start justify-between gap-3">
                          <CardTitle className="font-display text-base leading-tight">
                            {t.title}
                          </CardTitle>
                          <ChevronRight
                            className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                            aria-hidden
                          />
                        </div>
                        {t.description ? (
                          <CardDescription className="line-clamp-2">
                            {t.description}
                          </CardDescription>
                        ) : null}
                      </CardHeader>
                      <CardContent className="flex items-center gap-2 pt-0">
                        {t.season && t.season !== 'any' ? (
                          <Badge
                            variant="secondary"
                            className="font-mono text-[10px] tracking-wider"
                          >
                            {t.season}
                          </Badge>
                        ) : null}
                        {t.recurrenceRule ? (
                          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                            {t.recurrenceRule}
                          </span>
                        ) : null}
                      </CardContent>
                    </Card>
                  </Link>
                  {ownedByMe ? (
                    <Link
                      to={`/checklists/templates/${t.slug}/edit`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Edit ${t.title}`}
                      className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-md border border-rule bg-card/80 text-muted-foreground backdrop-blur transition-colors hover:border-primary/40 hover:bg-card hover:text-primary"
                      data-testid={`edit-${t.slug}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
